import { useMemo } from "react";
import { CompanyFilter } from "../../../legacy/types/companyFilter";
import { Market, Workspace } from "../../../types/workspace";
import { isRangeDefined } from "../utils";
import { useWorkspaceStore } from "../../../store/workspace/WorkspaceStore";

export const useConfigurationFilters = (
  workspace: Workspace | undefined,
  selectedMarket: Market
) => {
  const userFilters = useWorkspaceStore((state) => state.userFilters);

  return useMemo<CompanyFilter | undefined>(() => {
    if (!workspace) return undefined;

    // Apply the workspace-level company type filter (GHQ / Corporate Parent /
    // Domestic Parent). These flags are mutually exclusive; "All Entities" sets
    // none. Mirrors the precedence used by getCompanyTypeOption.
    const applyCompanyType = (filters: Partial<CompanyFilter>) => {
      if (userFilters?.isGhq) filters.isGhq = true;
      else if (userFilters?.isGp) filters.isGp = true;
      else if (userFilters?.isDp) filters.isDp = true;
    };

    if (selectedMarket === Market.TAM) {
      const tamFilters: CompanyFilter = {};

      // Add opportunity type filter if requested and available
      if (userFilters?.opportunityType) {
        tamFilters.opportunityType = userFilters.opportunityType;
      }

      applyCompanyType(tamFilters);

      return tamFilters;
    }

    const { industry, geography } = workspace.configuration[Market.SAM];

    const samFilters: Partial<CompanyFilter> = {
      industry: {
        industryIds: industry.ids,
        excludedIndustryIds: industry.excludedIds,
        naics2012Ids: industry.naicsIds,
        excludedNaics2012Ids: industry.excludedNaicsIds,
        sicUS1987Ids: industry.sicIds,
        excludedSicUS1987Ids: industry.excludedSicIds,
      },
      countryAlpha2s: geography.countryIds,
      excludedCountryAlpha2s: geography.excludedCountryIds,
      regionNames: geography.regionsIds,
      stateIds: geography.stateIds,
    };

    // Add opportunity type filter if requested and available
    if (userFilters?.opportunityType) {
      samFilters.opportunityType = userFilters.opportunityType;
    }

    applyCompanyType(samFilters);

    if (selectedMarket === Market.SAM) return samFilters;
    const {
      employeeRange,
      revenueRange,
      productIds,
      productsInclusionMethod,
      excludedProductIds,
      excludedProductsInclusionMethod,
    } = workspace.configuration[Market.SOM];
    const somFilters: Partial<CompanyFilter> = {
      ...samFilters,
      employees: !isRangeDefined(employeeRange) ? undefined : employeeRange,
      revenue: !isRangeDefined(revenueRange) ? undefined : revenueRange,
      products: [
        { ids: productIds, inclusionMethod: productsInclusionMethod },
        {
          ids: excludedProductIds,
          inclusionMethod: excludedProductsInclusionMethod,
        },
      ],
    };

    // Add opportunity type filter if requested and available
    if (userFilters?.opportunityType) {
      somFilters.opportunityType = userFilters.opportunityType;
    }

    return somFilters;
  }, [workspace, selectedMarket, userFilters]);
};
